import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { DndProvider, useDrag, useDrop, useDragLayer } from "react-dnd";
import { HTML5Backend, getEmptyImage } from "react-dnd-html5-backend";
import { agentsData } from "Stores/agentsData";
import BrandedBootLoader from "Components/common/BrandedBootLoader";
import BreadCrumbComponent from "Components/common/breadcrumb";
import AgentSelect from "./AgentSelect";
import { daysOf, scheduleText as scheduleLabel } from "./DayOfWeekField";

const DND_TYPE = "moveAgent";
const ALL = "__all";

const statusMeta = {
  ACTIVE: { label: "Live", cls: "ag-pill--live" },
  PAUSED: { label: "Paused", cls: "ag-pill--draft" },
  DRAFT: { label: "Draft", cls: "ag-pill--draft" },
  ARCHIVED: { label: "Archived", cls: "ag-pill--draft" }
};

const agentType = a => {
  const t = a.trigger && a.trigger.type;
  if (t === "schedule.recurring") return "Scheduled";
  if (t && t.startsWith("event.")) return "Event";
  return "Custom";
};

const scheduleText = a => {
  const t = a.trigger;
  if (!t) return "Manual";
  if (t.type === "schedule.recurring") {
    const cfg = t.config || {};
    return `Runs ${scheduleLabel(cfg.frequency, cfg.time, daysOf(cfg), (a.config && a.config.timezone) || "UTC")}`;
  }
  if (t.type && t.type.startsWith("event.")) return `On ${t.type.slice(6).replace(/_/g, " ").toLowerCase()}`;
  return "Manual";
};

// One consistent, subtle bot glyph for every agent.
const BotGlyph = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4.5" y="8" width="15" height="10.5" rx="3.2" />
    <path d="M12 8V5" />
    <circle cx="12" cy="3.6" r="1.15" fill="currentColor" stroke="none" />
    <circle cx="9.3" cy="13" r="1.05" fill="currentColor" stroke="none" />
    <circle cx="14.7" cy="13" r="1.05" fill="currentColor" stroke="none" />
    <path d="M2.8 12v3M21.2 12v3" />
  </svg>
);

// Small drag chip that follows the cursor (keeps the folder column visible).
const AgentDragLayer = () => {
  const { isDragging, item, offset } = useDragLayer(m => ({
    isDragging: m.isDragging(),
    item: m.getItem(),
    offset: m.getClientOffset()
  }));
  if (!isDragging || !offset || !item) return null;
  return (
    <div className="ag-draglayer">
      <div className="ag-dragchip" style={{ transform: `translate(${offset.x + 12}px, ${offset.y + 12}px)` }}>
        <span className="ag-dragchip__icon"><BotGlyph size={15} /></span>
        <span className="ag-dragchip__name">{item.name}</span>
      </div>
    </div>
  );
};

const AgentCard = ({ a, history, onArchive, onUnarchive, onDelete }) => {
  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: DND_TYPE,
    item: { id: a.id, folder: a.folder || "", name: a.name },
    collect: m => ({ isDragging: m.isDragging() })
  });
  useEffect(() => { previewRef(getEmptyImage(), { captureDraggingState: true }); }, [previewRef]);
  // Menu is rendered in a portal (see below): the card has a hover `transform`, which would
  // otherwise make the menu's fixed positioning resolve against the card and get clipped by
  // its `overflow:hidden`. Position is captured from the button's rect when opened.
  const [menuPos, setMenuPos] = useState(null); // null | { top, left }
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const meta = statusMeta[a.status] || statusMeta.DRAFT;
  const archived = a.status === "ARCHIVED";

  const toggleMenu = e => {
    e.stopPropagation();
    if (menuPos) { setMenuPos(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 6, left: Math.max(8, r.right - 156) });
  };
  const act = fn => e => { e.stopPropagation(); setMenuPos(null); fn(a); };

  // While open, close on any outside interaction. We can't rely on stopPropagation inside the
  // menu: React 16 delegates events at `document`, where this listener also lives, so a menu
  // click would still reach it. Instead, ignore mousedowns that land on the menu or its button.
  useEffect(() => {
    if (!menuPos) return undefined;
    const close = e => {
      if (e && (
        (menuRef.current && menuRef.current.contains(e.target)) ||
        (btnRef.current && btnRef.current.contains(e.target))
      )) return;
      setMenuPos(null);
    };
    const onKey = e => { if (e.key === "Escape") setMenuPos(null); };
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuPos]);

  return (
    <div
      ref={dragRef}
      className="ag-card"
      style={{ opacity: isDragging ? 0.5 : 1, cursor: isDragging ? "grabbing" : "pointer" }}
      onClick={() => history.push(`/agents/${a.id}`)}
    >
      <span className="ag-card__drag material-icons-outlined">drag_indicator</span>
      <button ref={btnRef} className="ag-card__menubtn material-icons-outlined"
        title="More actions"
        onMouseDown={e => e.stopPropagation()}
        onClick={toggleMenu}>more_vert</button>
      {menuPos && createPortal(
        <div ref={menuRef} className="ag-menu" style={{ top: menuPos.top, left: menuPos.left }}
          onClick={e => e.stopPropagation()}>
          {archived
            ? <button className="ag-menu__item" onClick={act(onUnarchive)}>
                <i className="material-icons-outlined">unarchive</i>Restore</button>
            : <button className="ag-menu__item" onClick={act(onArchive)}>
                <i className="material-icons-outlined">archive</i>Archive</button>}
          <button className="ag-menu__item ag-menu__item--danger" onClick={act(onDelete)}>
            <i className="material-icons-outlined">delete_outline</i>Delete</button>
        </div>,
        document.body
      )}
      <div className="ag-card__row1">
        <span className="ag-card__icon"><BotGlyph size={20} /></span>
        <div className="ag-card__title">{a.name}</div>
      </div>
      <div className="ag-card__meta">
        <span className={`ag-pill ${meta.cls}`}><span className="ag-dot" />{meta.label}</span>
        <span className="ag-typechip">{agentType(a)}</span>
      </div>
      {/* What it's for, in a sentence. Written server-side from the flow; absent on an agent
          whose summary hasn't been produced yet, and the card simply reads as it did before. */}
      {/* Clamped to two lines on the card; the title carries the rest for a hover. */}
      {!!a.summary && <div className="ag-card__summary" title={a.summary}>{a.summary}</div>}
      <div className="ag-card__sched">{scheduleText(a)}</div>
      {/* Only ICP agents need an approved ICP — icpApproved defaults to false on every agent. */}
      {a.config && a.config.icpApproved === false && a.status !== "ACTIVE" && agentType(a) === "Scheduled"
        && (a.templateKey === "daily_icp_prospector" || a.config.sellerProfileId) && (
        <div className="ag-card__warn">ICP not approved yet</div>
      )}
    </div>
  );
};

const FolderRow = ({ id, label, icon, active, onSelect, onDropAgent, canEdit, onRename, onDelete }) => {
  const [{ isOver }, dropRef] = useDrop({
    accept: DND_TYPE,
    drop: item => onDropAgent(item, id),
    collect: m => ({ isOver: m.isOver() && m.canDrop() })
  });
  return (
    <div
      ref={dropRef}
      className={`ag-folder ${active ? "selected" : ""} ${isOver ? "dropOver" : ""}`}
      onClick={() => onSelect(id)}
    >
      <i className="material-icons-outlined">{icon}</i>
      <span className="ag-folder__name">{label}</span>
      {isOver && <span className="material-icons-outlined ag-folder__drop">drag_handle</span>}
      {canEdit && !isOver && (
        <span className="ag-folder__actions">
          <i className="material-icons-outlined" title="Rename" onClick={e => { e.stopPropagation(); onRename(id); }}>edit</i>
          <i className="material-icons-outlined" title="Remove folder" onClick={e => { e.stopPropagation(); onDelete(id); }}>close</i>
        </span>
      )}
    </div>
  );
};

// Filters persist across navigation so returning to the page keeps the user's view.
const LS = { q: "agents.filter.q", status: "agents.filter.status", type: "agents.filter.type", folder: "agents.filter.folder" };
const readLS = (k, d) => { try { const v = localStorage.getItem(k); return v === null ? d : v; } catch (e) { return d; } };

const AgentsList = observer(() => {
  const history = useHistory();
  // Check the team's agent limit before opening the new-agent page.
  const hire = async () => { if (await agentsData.canHireAgent()) history.push("/agents/new"); };
  const [q, setQ] = useState(() => readLS(LS.q, ""));
  const [status, setStatus] = useState(() => readLS(LS.status, "ACTIVE")); // default to Live agents
  const [type, setType] = useState(() => readLS(LS.type, "All"));
  const [activeFolder, setActiveFolder] = useState(() => readLS(LS.folder, ALL));
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const [loaded, setLoaded] = useState(false);

  // Persist filter changes to the browser.
  useEffect(() => {
    try {
      localStorage.setItem(LS.q, q);
      localStorage.setItem(LS.status, status);
      localStorage.setItem(LS.type, type);
      localStorage.setItem(LS.folder, activeFolder);
    } catch (e) { /* ignore storage failures (private mode, etc.) */ }
  }, [q, status, type, activeFolder]);

  useEffect(() => {
    (async () => { await agentsData.fetchAgents(); setLoaded(true); })();
    agentsData.fetchUsage();
    agentsData.fetchApprovalCount();
    agentsData.fetchFolders();
  }, []);

  const { agents, agentsLoading, pendingApprovalCount } = agentsData;

  // First-run: only AFTER the fetch resolves (not the initial empty render), if there are
  // truly no agents, take the user straight to the "hire an agent" page.
  useEffect(() => {
    if (loaded && agents.length === 0) history.replace("/agents/new");
  }, [loaded, agents.length, history]);

  // Folders are team-shared and DB-backed (listFolders returns the union of created folders
  // and any in use by agents, so empty folders persist too).
  const folderNames = agentsData.folders || [];

  const query = q.trim().toLowerCase();
  const visible = agents.filter(a => {
    if (activeFolder !== ALL && (a.folder || "").trim() !== activeFolder) return false;
    if (status !== "ALL" && a.status !== status) return false;
    if (type !== "All" && agentType(a) !== type) return false;
    if (query && !String(a.name || "").toLowerCase().includes(query)) return false;
    return true;
  });

  const onDropAgent = async (item, target) => {
    const dest = target === ALL ? "" : target;
    if ((item.folder || "") === dest) return;
    await agentsData.setFolder(item.id, dest);
  };
  const addFolder = async () => {
    const n = newName.trim();
    setNewName(""); setCreating(false);
    if (n && !folderNames.includes(n)) { await agentsData.createFolder(n); setActiveFolder(n); }
  };
  const removeFolder = async name => {
    // Server-side: deletes the folder and clears it from any agents in it.
    await agentsData.deleteFolder(name);
    if (activeFolder === name) setActiveFolder(ALL);
  };

  const filtersActive = status !== "ALL" || type !== "All" || !!query || activeFolder !== ALL;
  const clearFilters = () => { setQ(""); setStatus("ALL"); setType("All"); setActiveFolder(ALL); };

  const archiveAgent = async a => { await agentsData.setStatus(a.id, "ARCHIVED"); };
  const unarchiveAgent = async a => { await agentsData.setStatus(a.id, "DRAFT"); };
  const deleteAgent = async a => {
    if (!window.confirm(`Delete “${a.name}”? It'll be removed from your list. Its run history is kept and it can be restored later.`)) return;
    await agentsData.deleteAgent(a.id);
    agentsData.fetchApprovalCount();
  };

  if (agentsLoading && agents.length === 0) {
    return <div className="ag-page"><BrandedBootLoader /></div>;
  }

  // No agents yet → the effect above redirects to /agents/new; show the loader meanwhile.
  if (!agentsLoading && agents.length === 0) {
    return <div className="ag-page"><BrandedBootLoader /></div>;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <AgentDragLayer />
      <div className="ag-shell">
        <div className="ag-shell__head">
          <BreadCrumbComponent breadCrumbValue={[
            { name: "Agents", link: "/agents" },
            { name: activeFolder === ALL ? "All agents" : activeFolder, link: "/agents" }
          ]} />
          <div className="ag-actions">
            <button className="ag-btn" onClick={() => history.push("/agents/approvals")}>
              Approvals
              {pendingApprovalCount > 0 && <span className="ag-nav-badge">{pendingApprovalCount}</span>}
            </button>
            {agentsData.usage && (
              <span className="ag-usage" title="Agents your team has, of the number it can have">
                {agentsData.usage.used} of {agentsData.usage.eligible} agents
              </span>
            )}
            <button className="ag-btn ag-btn--primary" onClick={hire}>+ Hire an agent</button>
          </div>
        </div>

        <div className="ag-shell__body">
          <aside className="ag-folders">
            <FolderRow id={ALL} label="All agents" icon="folder" active={activeFolder === ALL}
              onSelect={setActiveFolder} onDropAgent={onDropAgent} />
            {folderNames.map(f => (
              <FolderRow key={f} id={f} label={f} icon="folder" active={activeFolder === f}
                onSelect={setActiveFolder} onDropAgent={onDropAgent} canEdit
                onRename={async () => { const nn = window.prompt("Rename folder", f); if (nn && nn.trim() && nn.trim() !== f) {
                  await agentsData.renameFolder(f, nn.trim());
                  if (activeFolder === f) setActiveFolder(nn.trim());
                } }}
                onDelete={removeFolder} />
            ))}
            {creating ? (
              <div className="ag-folder ag-folder--new">
                <input className="ag-input" autoFocus placeholder="Folder name" value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") addFolder(); if (e.key === "Escape") { setCreating(false); setNewName(""); } }}
                  onBlur={addFolder} />
              </div>
            ) : (
              <button className="ag-folder ag-folder--add" onClick={() => setCreating(true)}>
                <i className="material-icons-outlined">add</i>
                <span>New folder</span>
              </button>
            )}
          </aside>

          <div className="ag-shell__main">
            <div className="ag-toolbar">
              <input className="ag-input ag-toolbar__search" placeholder="Search agents…" value={q} onChange={e => setQ(e.target.value)} />
              <div className="ag-toolbar__filter">
                <AgentSelect value={status} onChange={setStatus}
                  options={[
                    { value: "ALL", label: "All statuses" },
                    { value: "ACTIVE", label: "Live" },
                    { value: "DRAFT", label: "Draft" },
                    { value: "PAUSED", label: "Paused" },
                    { value: "ARCHIVED", label: "Archived" }
                  ]} />
              </div>
              <div className="ag-toolbar__filter">
                <AgentSelect value={type} onChange={setType}
                  options={[
                    { value: "All", label: "All types" },
                    { value: "Scheduled", label: "Scheduled" },
                    { value: "Event", label: "Event" },
                    { value: "Custom", label: "Custom" }
                  ]} />
              </div>
            </div>

            {visible.length === 0 ? (
              <div className="ag-empty">
                <div className="ag-empty__icon"><BotGlyph size={30} /></div>
                <div className="ag-empty__title">No agents here</div>
                <div className="ag-empty__text">
                  {filtersActive
                    ? "No agents match your current filters. Try clearing them, or hire a new one."
                    : "Hire your first agent to automate prospecting, follow-ups and more."}
                </div>
                <div className="ag-empty__actions">
                  {filtersActive && <button className="ag-btn" onClick={clearFilters}>Clear filters</button>}
                  <button className="ag-btn ag-btn--primary" onClick={hire}>+ Hire an agent</button>
                </div>
              </div>
            ) : (
              <div className="ag-grid">
                {visible.map(a => <AgentCard key={a.id} a={a} history={history}
                  onArchive={archiveAgent} onUnarchive={unarchiveAgent} onDelete={deleteAgent} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </DndProvider>
  );
});

export default AgentsList;
